import { ModuleFE_Advisor_Value, ModuleFE_Advisor_Variable } from '@app/advisor-frontend';
import { filterInstances, MissingDataException, MUSTNeverHappenException } from '@nu-art/ts-common';
import { valueType_Enumerated, valueType_Range, valueType_StandardRange, valueType_Tuple, variableType_Attribute, variableType_PrecipitatingFactor, variableType_RelievingFactor } from '@app/hcs-shared';
//######################### Calculation Aid functions #########################
const variableIsExclusive = (variable) => {
    const value = ModuleFE_Advisor_Value.cache.unique(variable.valueId);
    if (!value)
        throw new MUSTNeverHappenException(`Could not find value for _id ${variable.valueId}`);
    //Tuple is by definition exclusive
    if (value.type === valueType_Tuple)
        return true;
    //Enumerated is exclusive if it says so in the prop
    if (value.type === valueType_Enumerated)
        return !!value.data.props.isExclusive;
    //For now, any other value type is non-exclusive
    return false;
};
const getVariableAttributes = (formVar) => {
    if (!formVar.attributes?.length)
        return;
    const attributes = formVar.attributes.map(att => {
        const attribute = ModuleFE_Advisor_Variable.cache.unique(att.id);
        if (!attribute)
            throw new MUSTNeverHappenException(`Could not find variable for id ${att.id}`);
        return attribute;
    });
    const parentMap = attributes.reduce((acc, att) => {
        const parentId = att.parents.attributeId;
        if (!parentId || acc[parentId])
            return acc;
        const parent = ModuleFE_Advisor_Variable.cache.unique(parentId);
        if (!parent)
            throw new MUSTNeverHappenException(`Could not find variable for id ${parentId}`);
        acc[parentId] = parent;
        return acc;
    }, {});
    return {
        regular: attributes.filter(att => parentMap[att.parents.attributeId].type === variableType_Attribute),
        precipitating: attributes.filter(att => parentMap[att.parents.attributeId].type === variableType_PrecipitatingFactor),
        relieving: attributes.filter(att => parentMap[att.parents.attributeId].type === variableType_RelievingFactor),
    };
};
//######################### Tree Calculation #########################
export const calculateWizardTree_Variable = (formVar, editable, config) => {
    let children;
    const variable = ModuleFE_Advisor_Variable.cache.unique(formVar.id);
    if (!variable)
        throw new MUSTNeverHappenException(`Could not find variable for id ${formVar.id}`);
    const exclusiveVariable = variableIsExclusive(variable);
    const associatedAttributes = getVariableAttributes(formVar);
    //Forcing only attribute children for now, until EM supports multi select
    // eslint-disable-next-line no-constant-condition
    if (exclusiveVariable || true) {
        //Editing a singular value
        children = [];
        const editableValue = editable.editProp(0, {});
        const editableAttributes = editableValue.editProp('attributes', {});
        const getEditableAttribute = (att) => {
            const attId = att._id;
            const editableAtt = editableAttributes.editProp(attId, []);
            editableAtt.setOnDelete(() => editableAttributes.updateObj({ [attId]: undefined }));
            return editableAtt;
        };
        //Force the attribute are disabled if no value is selected
        const _config = { ...config, disabled: config.disabled || !editableValue.item.valueId };
        //Append regular attribute children
        if (associatedAttributes?.regular.length)
            children.push(...associatedAttributes.regular.map(att => calculateWizardTree_Attribute(att, getEditableAttribute(att), _config)));
        //Append precipitating factor attribute children
        if (associatedAttributes?.precipitating.length) {
            const _children = associatedAttributes.precipitating.map(att => calculateWizardTree_Attribute(att, getEditableAttribute(att), _config));
            _children.forEach(child => child.type = 'precipitating-factor');
            children.push({
                type: 'precipitating-factors-group',
                item: {},
                _children,
            });
        }
        //Append relieving factor attribute children
        if (associatedAttributes?.relieving.length) {
            const _children = associatedAttributes.relieving.map(att => calculateWizardTree_Attribute(att, getEditableAttribute(att), _config));
            _children.forEach(child => child.type = 'relieving-factor');
            children.push({
                type: 'relieving-factors-group',
                item: {},
                _children
            });
        }
    }
    else {
        //Variable is not exclusive, children can only be values
        //At this point we render children only if data actually exists
        if (editable.item.length)
            children = filterInstances(editable.item.map((valData, index) => {
                //We don't render nodes if we don't have the value ID
                if (!valData?.valueId)
                    return;
                const editableValue = editable.editProp(index, {});
                editableValue.setOnDelete(() => editable.removeArrayItem(index));
                return calculateWizardTree_Value(editableValue, config, associatedAttributes);
            }));
    }
    const value = ModuleFE_Advisor_Value.cache.unique(variable.valueId);
    if (!value)
        throw new MissingDataException(`Could not find value for id ${variable.valueId} for variable with id ${variable._id}`);
    const numericValueTypes = [valueType_StandardRange, valueType_Range];
    return {
        type: 'variable',
        item: {
            id: formVar.id,
            inputMethod: numericValueTypes.includes(value.type) ? 'raw' : 'value',
            isCommon: !!formVar.isCommon,
            onFocus: config.onFocus,
            focusedPath: config.focusedPath,
            editable: editable,
            disabled: config.disabled,
        },
        _children: children?.length ? children : undefined,
    };
};
const calculateWizardTree_Attribute = (attribute, editable, config) => {
    //TODO: When EM finally supports multi-select for attributes, we need to calculate children nodes for attributes
    return {
        type: 'attribute',
        item: {
            id: attribute._id,
            editable,
            disabled: config.disabled,
            onFocus: config.onFocus,
            focusedPath: config.focusedPath,
        }
    };
};
const calculateWizardTree_Value = (editable, config, attributes) => {
    if (!editable.item.valueId)
        return;
    let children;
    const editableAttributes = editable.editProp('attributes', {});
    const getEditableAttribute = (att) => {
        const attId = att._id;
        const editableAtt = editableAttributes.editProp(attId, []);
        editableAtt.setOnDelete(() => editableAttributes.updateObj({ [attId]: undefined }));
        return editableAtt;
    };
    if (attributes) {
        children = [];
        if (attributes.regular)
            children.push(...attributes.regular.map(att => calculateWizardTree_Attribute(att, getEditableAttribute(att), config)));
        if (attributes.precipitating)
            children.push({
                type: 'precipitating-factors-group',
                item: {},
                _children: attributes.precipitating.map(att => calculateWizardTree_Attribute(att, getEditableAttribute(att), config))
            });
        if (attributes.relieving)
            children.push({
                type: 'relieving-factors-group',
                item: {},
                _children: attributes.relieving.map(att => calculateWizardTree_Attribute(att, getEditableAttribute(att), config))
            });
    }
    return {
        type: 'value',
        item: {
            id: editable.item.valueId,
            editable: editable,
            disabled: config.disabled,
        },
        _children: children
    };
};
//# sourceMappingURL=variable-calculation.js.map